import React, { useState, useEffect, useMemo } from "react";
import groq from "groq";

import { useLiveQuery } from "@sanity/preview-kit";
import { sanityFetch } from "@natklok/util/sanity";
import PreviewProvider from "./PreviewProvider";
import { useSearchParams } from "react-router-dom";
import { CorrectOrIncorrect, Question, SanityQuestion } from "@common";
import { transformSanityQuestion } from "@natklok/sanity/transform/question";
import RenderQuestion from "../RenderQuestion";
import { SelectAnswerProvider } from "@natklok/contexts/selectAnswerContext";
import { SlideDataProvider } from "@natklok/contexts/slideDataContext";
import AnswerFeedback from "@natklok/slides/AnswerFeedback";
import { Tab, Tabs } from "@mui/material";

// sanity client

const token = import.meta.env.VITE_SANITY_READ_TOKEN ?? "";

export default function Preview({
  isFeedback,
  feedbackCorrectness = "incorrect",
}: {
  isFeedback?: boolean;
  feedbackCorrectness?: CorrectOrIncorrect;
}) {
  return (
    <PreviewProvider token={token}>
      <PreviewTest isFeedback={isFeedback} feedbackCorrectness={feedbackCorrectness} />
    </PreviewProvider>
  );
}
const createQuery = (id: string) => groq`*[questionBase.slug.current == "${id}"] {
  _id,
  _type,
  renderProps,
  "imageRenderProps": renderProps { image { asset->{ url } } },
  questionBase {
    title,
    slug {
      current
    },
    description,
    fitsAges,
    fitsProfiles,
    badge,
    correctResultTitle,
    correctResultDesc,
    incorrectResultTitle,
    incorrectResultDesc,
    readMore,
    youtubeVideo
  }
}
`;
export function PreviewTest({
  isFeedback,
  feedbackCorrectness = "incorrect",
}: {
  isFeedback?: boolean;
  feedbackCorrectness?: CorrectOrIncorrect;
}) {
  // get query id from url with react router
  const [searchParams] = useSearchParams();
  const slug = searchParams.get("slug") || "";

  const query = createQuery(slug);
  const [initialQuestions, setQuestions] = useState<SanityQuestion[]>([]);
  useEffect(() => {
    console.log("fetching data");
    const fetchData = async () => {
      const result = await sanityFetch<SanityQuestion[]>({
        query,
        previewDrafts: true,
      });
      setQuestions(result);
    };
    fetchData();
  }, [query]);
  const [data] = useLiveQuery(initialQuestions, query);

  const question = data[0];

  const transformedQuestion: Question | undefined = useMemo(
    () =>
      question === undefined
        ? undefined
        : {
            ...transformSanityQuestion(question),
            variant: {
              en: question._id + "Variant",
              sv: question._id + "Variant",
            },
          },
    [question]
  );

  const [value, setValue] = React.useState(0);

  if (!question) return <div>Loading...</div>;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <SlideDataProvider>
      <SelectAnswerProvider question={transformedQuestion}>
        <>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label="Question" />
            <Tab label="Correct answer" />
            <Tab label="Incorrect answer" />
          </Tabs>
          <CustomTabPanel value={value} index={0}>
            <RenderQuestion question={transformedQuestion} isPreview />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <AnswerFeedback
              answerDescription=""
              slideData={{
                question: transformedQuestion,
                state: "correct",
                completed: true,
                savedInStorage: false,
              }}
              correctOrIncorrect="correct"
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <AnswerFeedback
              answerDescription=""
              slideData={{
                question: transformedQuestion,
                state: "incorrect",
                completed: true,
                savedInStorage: false,
              }}
              correctOrIncorrect="incorrect"
            />
          </CustomTabPanel>
        </>
      </SelectAnswerProvider>
    </SlideDataProvider>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}
